/**
 * @fileoverview TOTP (Time-based One-Time Password) setup modal component.
 * Provides a guided wizard for configuring authenticator app-based two-factor authentication.
 */

import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Grid,
    Alert,
    IconButton,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    CircularProgress,
    Tooltip
} from "@mui/material";
import {
    DmsModal,
    DmsForm,
    DmsTextField,
    DmsButton,
    intl,
    fetchData,
    HTTP_STATUS
} from "dmsbasereact";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

/**
 * TOTP Setup Modal component
 *
 * A multi-step wizard modal for configuring TOTP-based two-factor authentication.
 * Guides users through the following steps:
 * 1. Scan QR code - Displays a QR code to scan with authenticator apps (Google Authenticator, Authy, Microsoft Authenticator, etc.)
 * 2. Verify code - User enters the 6-digit code from their authenticator app to confirm setup
 * 3. Complete - Success confirmation
 *
 * The modal also provides a manual entry option with the secret key for users who cannot scan QR codes.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isVisible - Controls modal visibility
 * @param {Function} props.onClose - Callback function when modal is closed
 * @param {string} props.otpauthUrl - The otpauth:// URL containing TOTP configuration and secret
 * @param {Function} props.onSuccess - Callback function executed after successful TOTP setup
 * @returns {JSX.Element|null} The TOTP setup modal or null if not visible
 */
export const TOTPSetupModal = ({ isVisible, onClose, otpauthUrl, onSuccess }) => {
    const [activeStep, setActiveStep] = useState(0);
    const [verifying, setVerifying] = useState(false);
    const [error, setError] = useState(null);
    const [secretCopied, setSecretCopied] = useState(false);
    const [qrCodeLoading, setQrCodeLoading] = useState(true);
    const [qrCodeError, setQrCodeError] = useState(false);

    // Extrai o secret da otpauth URL para exibição manual
    const extractSecret = (url) => {
        if (!url) return null;
        const match = url.match(/secret=([A-Z0-9]+)/);
        return match ? match[1] : null;
    };

    const secret = extractSecret(otpauthUrl);

    // Generate QR code URL using QuickChart API (more reliable than Google Charts)
    const getQrCodeUrl = (data) => {
        if (!data) return null;
        // QuickChart is a reliable free QR code API
        return `https://quickchart.io/qr?text=${encodeURIComponent(data)}&size=200&margin=2`;
    };

    const qrCodeUrl = getQrCodeUrl(otpauthUrl);

    // Handle QR code image loading
    const handleQrCodeLoad = () => {
        setQrCodeLoading(false);
        setQrCodeError(false);
    };

    const handleQrCodeError = () => {
        setQrCodeLoading(false);
        setQrCodeError(true);
    };

    // Reset loading state when modal opens
    useEffect(() => {
        if (isVisible && otpauthUrl) {
            setQrCodeLoading(true);
            setQrCodeError(false);
        }
    }, [isVisible, otpauthUrl]);

    const handleCopySecret = () => {
        if (secret) {
            navigator.clipboard.writeText(secret);
            setSecretCopied(true);
            setTimeout(() => setSecretCopied(false), 3000);
        }
    };

    const handleVerify = async (data) => {
        setVerifying(true);
        setError(null);

        try {
            const response = await fetchData("/mfa/totp/verify_token", "POST", true, {
                token: data.verification_code
            });

            if (response.status === HTTP_STATUS.OK_200 && response.data?.data?.valid) {
                setActiveStep(2); // Move to success step
                setTimeout(() => {
                    if (onSuccess) onSuccess();
                    handleClose();
                }, 2000);
            } else {
                setError(intl.get("InvalidTOTPCode").d("Invalid verification code. Please check and try again."));
            }
        } catch (err) {
            console.error("Error verifying TOTP:", err);
            setError(intl.get("TOTPVerificationError").d("Error verifying code. Please try again."));
        } finally {
            setVerifying(false);
        }
    };

    const handleClose = () => {
        setActiveStep(0);
        setError(null);
        setSecretCopied(false);
        onClose();
    };

    const handleNext = () => {
        setActiveStep((prevStep) => prevStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevStep) => prevStep - 1);
    };

    const steps = [
        {
            label: intl.get("ScanQRCode").d("Scan QR Code"),
            content: (
                <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {intl.get("TOTPStep1Description").d(
                            "Use your authenticator app (Google Authenticator, Authy, Microsoft Authenticator, etc.) to scan this QR code:"
                        )}
                    </Typography>

                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                        <Box
                            sx={{
                                border: '2px solid #ccc',
                                borderRadius: '8px',
                                padding: '16px',
                                backgroundColor: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: 232,
                                minHeight: 232,
                                position: 'relative'
                            }}
                        >
                            {qrCodeLoading && (
                                <CircularProgress size={40} />
                            )}
                            {qrCodeError && !qrCodeLoading && (
                                <Box sx={{ textAlign: 'center' }}>
                                    <ErrorOutlineIcon color="error" sx={{ fontSize: 48, mb: 1 }} />
                                    <Typography variant="caption" color="error" display="block">
                                        {intl.get("QRCodeError").d("Failed to generate QR code")}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {intl.get("UseManualCode").d("Please use the manual code below")}
                                    </Typography>
                                </Box>
                            )}
                            {qrCodeUrl && (
                                <img
                                    src={qrCodeUrl}
                                    alt="TOTP QR Code"
                                    onLoad={handleQrCodeLoad}
                                    onError={handleQrCodeError}
                                    style={{
                                        display: qrCodeLoading || qrCodeError ? 'none' : 'block',
                                        width: 200,
                                        height: 200
                                    }}
                                />
                            )}
                        </Box>
                    </Box>

                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="caption">
                            {intl.get("TOTPManualEntry").d("Can't scan? Enter this code manually in your app:")}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontFamily: 'monospace',
                                    backgroundColor: 'background.paper',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    flexGrow: 1
                                }}
                            >
                                {secret}
                            </Typography>
                            <Tooltip title={intl.get("Tooltip.Action.Copy").d("Copiar")}>
                                <IconButton
                                    size="small"
                                    onClick={handleCopySecret}
                                    color={secretCopied ? "success" : "default"}
                                >
                                    {secretCopied ? <CheckCircleIcon /> : <ContentCopyIcon />}
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Alert>

                    <Box sx={{ mt: 2 }}>
                        <DmsButton variant="primary" onClick={handleNext} fullWidth>
                            {intl.get("Next").d("Next")}
                        </DmsButton>
                    </Box>
                </Box>
            )
        },
        {
            label: intl.get("VerifyCode").d("Verify Code"),
            content: (
                <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {intl.get("TOTPStep2Description").d(
                            "Enter the 6-digit code from your authenticator app to verify the setup:"
                        )}
                    </Typography>

                    <DmsForm onSubmit={handleVerify}>
                        <DmsForm.Body>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <DmsTextField
                                        name="verification_code"
                                        label={intl.get("VerificationCode").d("Verification Code")}
                                        placeholder="000000"
                                        fullWidth
                                        required
                                        inputProps={{
                                            maxLength: 6,
                                            pattern: "[0-9]{6}",
                                            inputMode: "numeric"
                                        }}
                                        helperText={intl.get("TOTPCodeHelp").d("Enter the 6-digit code from your app")}
                                    />
                                </Grid>
                            </Grid>

                            {error && (
                                <Alert severity="error" sx={{ mt: 2 }}>
                                    {error}
                                </Alert>
                            )}

                            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                                <DmsButton
                                    variant="secondary"
                                    onClick={handleBack}
                                    disabled={verifying}
                                    fullWidth
                                >
                                    {intl.get("Back").d("Back")}
                                </DmsButton>
                                <DmsButton
                                    variant="primary"
                                    type="submit"
                                    disabled={verifying}
                                    fullWidth
                                >
                                    {verifying
                                        ? intl.get("Verifying").d("Verifying...")
                                        : intl.get("Verify").d("Verify")
                                    }
                                </DmsButton>
                            </Box>
                        </DmsForm.Body>
                    </DmsForm>
                </Box>
            )
        },
        {
            label: intl.get("Complete").d("Complete"),
            content: (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                    <CheckCircleIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                        {intl.get("TOTPSetupComplete").d("TOTP Setup Complete!")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {intl.get("TOTPSetupCompleteDescription").d(
                            "Your authenticator app has been successfully configured. You can now use it to authenticate."
                        )}
                    </Typography>
                </Box>
            )
        }
    ];

    if (!isVisible) return null;

    return (
        <DmsModal isVisible={isVisible} onClose={handleClose}>
            <Box sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                    {intl.get("SetupAuthenticatorApp").d("Setup Authenticator App (TOTP)")}
                </Typography>

                <Stepper activeStep={activeStep} orientation="vertical">
                    {steps.map((step, index) => (
                        <Step key={step.label}>
                            <StepLabel>{step.label}</StepLabel>
                            <StepContent>
                                {step.content}
                            </StepContent>
                        </Step>
                    ))}
                </Stepper>

                {activeStep === steps.length && (
                    <Box sx={{ mt: 2 }}>
                        <DmsButton variant="primary" onClick={handleClose} fullWidth>
                            {intl.get("Close").d("Close")}
                        </DmsButton>
                    </Box>
                )}
            </Box>
        </DmsModal>
    );
};
